import React, { Component } from "react";
import { CallAPI, GetCookieFunction } from '../../utils/util';
import { useSearchParams } from "react-router-dom";

function withRouter(Component) {
    function ComponentWithRouterProp(props) {
        let [searchParams] = useSearchParams();
        return <Component {...props} searchParams={searchParams} />;
    }
    return ComponentWithRouterProp;
}

class WeeklyScheduleInner extends Component {
    constructor(props) {
        super(props);
        this.centerId = GetCookieFunction("Center").id;
        this.isAdmin = GetCookieFunction("User").type === "centeradmin" || GetCookieFunction("User").type === "executive";

        // Use query param or default to current week's Saturday
        const weekStartParam = this.props.searchParams.get('weekStart');
        let weekStart;
        if (weekStartParam) {
            weekStart = weekStartParam;
        } else {
            const today = new Date();
            const day = today.getDay(); // 0=Sun, 6=Sat
            const diff = day >= 6 ? 0 : -(day + 1);
            const saturday = new Date(today);
            saturday.setDate(today.getDate() + diff);
            weekStart = `${saturday.getFullYear()}-${String(saturday.getMonth()+1).padStart(2,'0')}-${String(saturday.getDate()).padStart(2,'0')}`;
        }

        this.state = {
            weekStart,
            slots: [],
            schedule: [],
            capacity: [],
            availableMachines: 0,
            loading: true
        };
    }

    componentDidMount() {
        this.loadWeekData();
    }

    loadWeekData = () => {
        const { weekStart } = this.state;

        // Load slots
        CallAPI({}, `scheduling/slots?centerId=${this.centerId}`, 'GET', (res) => {
            if (res.success) this.setState({ slots: res.data });
        });

        // Load weekly schedule
        CallAPI({}, `scheduling/weekly?centerId=${this.centerId}&weekStart=${weekStart}`, 'GET', (res) => {
            if (res.success) this.setState({ schedule: res.data, loading: false });
            else this.setState({ loading: false });
        });

        // Load capacity grid
        CallAPI({}, `scheduling/capacity?centerId=${this.centerId}&weekStart=${weekStart}`, 'GET', (res) => {
            if (res.success) this.setState({ capacity: res.data });
        });

        // Load machine count
        CallAPI({}, `scheduling/machines?centerId=${this.centerId}`, 'GET', (res) => {
            if (res.success) this.setState({ availableMachines: res.availableMachines });
        });
    }

    navigateWeek = (direction) => {
        const parts = this.state.weekStart.split('-');
        const d = new Date(parts[0], parts[1]-1, parts[2]);
        d.setDate(d.getDate() + (direction * 7));
        const ws = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        this.setState({ weekStart: ws, loading: true, expandedCell: null }, this.loadWeekData);
    }

    getDaysOfWeek = () => {
        const days = [];
        const parts = this.state.weekStart.split('-');
        const start = new Date(parts[0], parts[1]-1, parts[2]);
        for (let i = 0; i < 7; i++) {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            days.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
        }
        return days;
    }

    getBookedCount = (slotId, dateStr) => {
        return this.state.capacity.filter(c =>
            c.slotId === slotId && c.scheduleDate && c.scheduleDate.startsWith(dateStr)
        ).reduce((sum, c) => sum + c.booked, 0);
    }

    getPatientsForCell = (slotId, dateStr) => {
        return this.state.schedule.filter(s =>
            s.slotId === slotId && s.scheduleDate && s.scheduleDate.startsWith(dateStr)
        );
    }

    handleCancelBooking = (scheduleId) => {
        if (!window.confirm("Cancel this appointment?")) return;
        CallAPI({}, `scheduling/book/${scheduleId}?centerId=${this.centerId}`, 'DELETE', (res) => {
            if (res.success) this.loadWeekData();
        });
    }

    formatTime = (timeStr) => {
        if (!timeStr) return "";
        const [h, m] = timeStr.split(":");
        const hour = parseInt(h, 10);
        const ampm = hour >= 12 ? "PM" : "AM";
        const h12 = hour % 12 || 12;
        return `${h12}:${m} ${ampm}`;
    }

    formatShortDate = (dateStr) => {
        const parts = dateStr.substring(0, 10).split('-');
        const d = new Date(parts[0], parts[1]-1, parts[2]);
        return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    }

    formatWeekRange = () => {
        const parts = this.state.weekStart.split('-');
        const start = new Date(parts[0], parts[1]-1, parts[2]);
        const end = new Date(start);
        end.setDate(start.getDate() + 6);
        return `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }

    render() {
        const days = this.getDaysOfWeek();
        const { slots, availableMachines } = this.state;

        return (
            <div className="ci-page" style={{maxWidth: '1200px', overflow: 'visible'}}>
                <div className="ci-header">
                    <h2>Weekly Schedule</h2>
                    <p>View dialysis appointments for the week</p>
                </div>

                {/* Week Navigator */}
                <div className="schedule-week-nav">
                    <button type="button" className="funnel-nav-arrow" onClick={() => this.navigateWeek(-1)}>←</button>
                    <span className="schedule-week-label">{this.formatWeekRange()}</span>
                    <button type="button" className="funnel-nav-arrow" onClick={() => this.navigateWeek(1)}>→</button>
                </div>

                {this.state.loading ? (
                    <div style={{textAlign: 'center', padding: '40px', color: 'var(--text-muted)'}}>Loading...</div>
                ) : slots.length === 0 ? (
                    <div style={{textAlign: 'center', padding: '40px', color: 'var(--text-muted)'}}>No time slots configured for this center.</div>
                ) : (
                    <div className="schedule-grid-wrap">
                        <table className="schedule-grid">
                            <thead>
                                <tr>
                                    <th className="schedule-grid-slot-header">Slot</th>
                                    {days.map(d => (
                                        <th key={d} className="schedule-grid-day-header">
                                            <a href={`/schedule/daily?date=${d}`} style={{color: 'inherit', textDecoration: 'none'}}>
                                                {this.formatShortDate(d)}
                                            </a>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {slots.map(slot => (
                                    <tr key={slot.slotId}>
                                        <td className="schedule-grid-slot-label">
                                            {this.formatTime(slot.startTime)}<br/>{this.formatTime(slot.endTime)}
                                        </td>
                                        {days.map(dateStr => {
                                            const booked = this.getBookedCount(slot.slotId, dateStr);
                                            const isOverbooked = booked > availableMachines;
                                            const isFull = booked === availableMachines && availableMachines > 0;
                                            const hasAvailability = booked < availableMachines;
                                            const cellKey = `${slot.slotId}-${dateStr}`;
                                            const isExpanded = this.state.expandedCell === cellKey;

                                            return (
                                                <td key={dateStr}
                                                    className={`schedule-grid-cell ${isOverbooked ? 'overbooked' : ''} ${isFull ? 'full' : ''} ${hasAvailability ? 'available' : ''}`}
                                                    onClick={() => window.location.href = `/schedule/daily?date=${dateStr}`}>
                                                    <div className="schedule-grid-count">{booked}/{availableMachines}</div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    }
}

export default withRouter(WeeklyScheduleInner);
